import { useEffect } from "react";
import { BUSINESS } from "../config/business";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEO({ title, description, keywords, path = "/", schema = [] }) {
  useEffect(() => {
    const fullTitle = title || `${BUSINESS.name} | ${BUSINESS.primaryKeyword}`;
    document.title = fullTitle;
    const canonical = `https://${BUSINESS.domain}${path}`;
    setMeta("description", description);
    setMeta("keywords", keywords);
    setLink("canonical", canonical);
    // Open Graph
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", BUSINESS.images.storefront, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", BUSINESS.images.storefront);

    // remove previous page schema
    document.querySelectorAll('script[data-page-schema="true"]').forEach((n) => n.remove());
    schema.forEach((s, i) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.pageSchema = "true";
      el.id = `page-schema-${i}`;
      el.textContent = JSON.stringify(s);
      document.head.appendChild(el);
    });
  }, [title, description, keywords, path, schema]);

  return null;
}

// Static org schema injected once
export function useOrganizationSchema() {
  useEffect(() => {
    const hoursSpec = BUSINESS.hours
      .filter((h) => h.open)
      .map((h) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" }[h.day],
        opens: h.open,
        closes: h.close,
      }));
    const org = {
      "@context": "https://schema.org",
      "@type": "AutoRepair",
      name: BUSINESS.name,
      image: BUSINESS.images.storefront,
      "@id": `https://${BUSINESS.domain}`,
      url: `https://${BUSINESS.domain}`,
      telephone: BUSINESS.phoneDisplay,
      email: BUSINESS.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.address.street,
        addressLocality: BUSINESS.address.city,
        addressRegion: BUSINESS.address.state,
        postalCode: BUSINESS.address.zip,
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: BUSINESS.geo.lat, longitude: BUSINESS.geo.lng },
      openingHoursSpecification: hoursSpec,
      areaServed: BUSINESS.cities.map((c) => c.name),
      knowsAbout: ["Smog Check", "STAR Smog Check", "Diesel Smog Check", "Smog Repair", "DMV Registration", "Title Transfer"],
    };
    if (BUSINESS.google.rating && BUSINESS.google.reviewCount) {
      org.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: BUSINESS.google.rating,
        reviewCount: BUSINESS.google.reviewCount,
      };
    }
    injectJsonLd("org-schema", org);
    injectJsonLd("website-schema", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: BUSINESS.name,
      url: `https://${BUSINESS.domain}`,
    });
  }, []);
}
